import React, { useState } from 'react';
import { Button, Form, Input, Card, message, Divider, Typography } from 'antd';
import {
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  MailOutlined
} from '@ant-design/icons';
import { apiClient } from '../api/client';
import { LoginRequest } from '../types/api';
import { forgotPassword } from '../api/auth';
import { APP_LOGO_URL as cleverooLogo } from '../constants/brand';

const { Title, Text, Link } = Typography;

// Add CSS animations
const animations = `
  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
  
  @keyframes glow {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.8; }
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .login-float {
    animation: float 6s ease-in-out infinite;
  }
  
  .login-card {
    transform: translateY(0);
    transition: transform 0.3s ease;
  }
  
  .login-card:hover {
    transform: translateY(-5px);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = animations;
  document.head.appendChild(styleSheet);
}

interface LoginFormData {
  email: string;
  password: string;
  remember?: boolean;
}

interface LoginUserData {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role?: string;
  studentId?: string;
  accessToken: string;
  refreshToken: string;
  provider?: string;
}

interface LoginProps {
  onLogin?: (userData: LoginUserData) => void;
  onSwitchToSignup?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin, onSwitchToSignup }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleLogin = async (values: LoginFormData) => {
    setLoading(true);
    try {
      console.log('🔐 Starting login with:', { email: values.email });
      
      const loginRequest: LoginRequest = {
        email: values.email,
        password: values.password
      };
      
      console.log('📤 Sending login request...');
      const response = await apiClient.login(loginRequest);
      console.log('📥 Login response received:', response);
      
      // Store tokens in localStorage
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      console.log('💾 Tokens stored in localStorage');
      
      message.success('Successfully signed in!');
      
      // Call parent component login callback
      const userData = {
        email: response.user.email,
        name: response.user.name,
        id: response.user.id,
        avatar: response.user.avatar,
        role: response.user.role,
        studentId: response.user.studentId,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken
      };
      
      console.log('👤 Calling onLogin with userData:', userData);
      console.log('🔗 onLogin function exists:', !!onLogin);
      
      if (onLogin) {
        onLogin(userData);
        console.log('✅ onLogin callback executed');
      } else {
        console.warn('⚠️ onLogin callback is not provided');
      }
    } catch (error: any) {
      console.error('❌ Login error:', error);
      message.error(error.message || 'Sign in failed. Please check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  const googleButtonRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!googleClientId) return;

    const initGoogle = () => {
      if (!window.google || !googleButtonRef.current) return false;

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response: any) => {
          setGoogleLoading(true);
          try {
            const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
            const apiResponse = await fetch(`${baseURL}/api/auth/google`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ credential: response.credential }),
            });

            const data = await apiResponse.json();

            if (data.success) {
              localStorage.setItem('accessToken', data.data.accessToken);
              localStorage.setItem('refreshToken', data.data.refreshToken);
              message.success('Successfully signed in with Google!');

              if (onLogin) {
                onLogin({
                  email: data.data.user.email,
                  name: data.data.user.name,
                  id: data.data.user.id,
                  avatar: data.data.user.avatar,
                  role: data.data.user.role,
                  studentId: data.data.user.studentId,
                  accessToken: data.data.accessToken,
                  refreshToken: data.data.refreshToken,
                  provider: 'google',
                });
              }
            } else {
              throw new Error(data.error || 'Google sign in failed');
            }
          } catch (error: any) {
            console.error('Google OAuth error:', error);
            message.error(error.message || 'Google sign in failed');
          } finally {
            setGoogleLoading(false);
          }
        },
      });

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        width: googleButtonRef.current.offsetWidth,
        text: 'continue_with',
        shape: 'pill',
      });
      return true;
    };

    // Try immediately, then poll until Google script loads
    if (!initGoogle()) {
      const interval = setInterval(() => {
        if (initGoogle()) clearInterval(interval);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [onLogin]);

  const handleForgotPassword = async () => {
    try {
      // Get email from form
      const email = form.getFieldValue('email');
      
      if (!email) {
        message.warning('Please enter your email address first');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        message.error('Please enter a valid email address');
        return;
      }

      // Call forgot password API
      await forgotPassword(email);
      message.success('Password reset email sent! Please check your inbox.');
    } catch (error: any) {
      message.error(error.message || 'Failed to send reset email. Please try again.');
    }
  };

  const handleRegister = () => {
    if (onSwitchToSignup) {
      onSwitchToSignup();
    }
  };

  return (
    <div 
      className="login-background flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradient 15s ease infinite'
      }}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute w-80 h-80 rounded-full opacity-20 login-float"
          style={{
            top: '-10rem',
            right: '-10rem',
            background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
            filter: 'blur(40px)'
          }}
        ></div>
        <div 
          className="absolute w-80 h-80 rounded-full opacity-20"
          style={{
            bottom: '-10rem',
            left: '-10rem',
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            filter: 'blur(40px)',
            animation: 'float 8s ease-in-out infinite',
            animationDelay: '1s'
          }}
        ></div>
        <div 
          className="absolute w-96 h-96 rounded-full opacity-10"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            filter: 'blur(60px)',
            animation: 'spin 20s linear infinite'
          }}
        ></div>
        <div 
          className="absolute w-32 h-32 rounded-full opacity-30"
          style={{
            top: '5rem',
            left: '5rem',
            background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            filter: 'blur(20px)',
            animation: 'float 4s ease-in-out infinite',
            animationDelay: '2s'
          }}
        ></div>
        <div 
          className="absolute w-24 h-24 rounded-full opacity-30"
          style={{
            bottom: '5rem',
            right: '5rem',
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            filter: 'blur(20px)',
            animation: 'float 6s ease-in-out infinite',
            animationDelay: '3s'
          }}
        ></div>
      </div>
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div 
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              paddingTop: '2rem'
            }}
          >
            <div 
              className="login-float"
              style={{
                position: 'relative',
                animation: 'float 6s ease-in-out infinite'
              }}
            >
              <div 
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '50%',
                  filter: 'blur(15px)',
                  opacity: 0.4,
                  animation: 'glow 2s ease-in-out infinite alternate'
                }}
              ></div>
              <img 
                src={cleverooLogo} 
                alt="Cleveroo Logo" 
                style={{
                  position: 'relative',
                  width: '80px',
                  height: '80px',
                  maxWidth: '80px',
                  maxHeight: '80px',
                  filter: 'drop-shadow(0 10px 15px rgba(0, 0, 0, 0.2))',
                  transition: 'transform 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              />
            </div>
          </div>
          <Title 
            level={2} 
            style={{
              fontSize: '2.5rem',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textAlign: 'center',
              marginBottom: '1rem',
              fontFamily: '"Inter", sans-serif'
            }}
          >
            Welcome Back to Cleveroo
          </Title>
          <Text 
            style={{
              color: '#6b7280',
              fontSize: '1.125rem',
              textAlign: 'center',
              display: 'block',
              marginBottom: '0.5rem'
            }}
          >
            Sign in to your account to continue your learning journey
          </Text>
        </div>

        {/* Login Card */}
        <Card 
          className="login-card shadow-2xl border-0 rounded-3xl"
          style={{
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.3)',
            borderRadius: '24px',
            backdropFilter: 'blur(20px)',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}
        >
          <Form
            form={form}
            name="login"
            onFinish={handleLogin}
            layout="vertical"
            size="large"
            requiredMark={false}
          >
            <Form.Item
              name="email"
              label={
                <span 
                  style={{
                    color: '#374151',
                    fontWeight: '600',
                    fontSize: '14px',
                    marginBottom: '8px'
                  }}
                >
                  Email Address
                </span>
              }
              rules={[
                { required: true, message: 'Please enter your email address' },
                { type: 'email', message: 'Please enter a valid email address' }
              ]}
              style={{ marginBottom: '20px' }}
            >
              <Input
                prefix={<MailOutlined style={{ color: '#9ca3af', fontSize: '16px' }} />}
                placeholder="Enter your email address"
                style={{
                  height: '52px',
                  borderRadius: '16px',
                  border: '2px solid #e5e7eb',
                  fontSize: '16px',
                  fontWeight: '400',
                  padding: '0 16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                  transition: 'all 0.2s ease-in-out'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={
                <span 
                  style={{
                    color: '#374151',
                    fontWeight: '600',
                    fontSize: '14px',
                    marginBottom: '8px'
                  }}
                >
                  Password
                </span>
              }
              rules={[
                { required: true, message: 'Please enter your password' },
                { min: 6, message: 'Password must be at least 6 characters' }
              ]}
              style={{ marginBottom: '20px' }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#9ca3af', fontSize: '16px' }} />}
                placeholder="Enter your password"
                style={{
                  height: '52px',
                  borderRadius: '16px',
                  border: '2px solid #e5e7eb',
                  fontSize: '16px',
                  fontWeight: '400',
                  padding: '0 16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                  transition: 'all 0.2s ease-in-out'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
                }}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem'
              }}
            >
              <Form.Item name="remember" valuePropName="checked" style={{ marginBottom: 0 }}>
                <label 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <input 
                    type="checkbox" 
                    style={{
                      marginRight: '12px',
                      width: '18px',
                      height: '18px',
                      accentColor: '#667eea',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  />
                  <span 
                    style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      fontWeight: '500',
                      transition: 'color 0.2s ease'
                    }}
                  >
                    Remember me
                  </span>
                </label>
              </Form.Item>
              <Link 
                onClick={handleForgotPassword}
                style={{
                  fontSize: '14px',
                  color: '#667eea',
                  fontWeight: '600',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#4f46e5';
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#667eea';
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                Forgot password?
              </Link>
            </div>

            <Form.Item style={{ marginBottom: '2rem' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  width: '100%',
                  height: '56px',
                  fontSize: '18px',
                  fontWeight: '700',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  boxShadow: '0 10px 25px -5px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 15px 35px -5px rgba(102, 126, 234, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(102, 126, 234, 0.4)';
                }}
              >
                <span 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {loading ? (
                    <>
                      <div 
                        style={{
                          width: '20px',
                          height: '20px',
                          border: '2px solid #ffffff',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          marginRight: '8px'
                        }}
                      ></div>
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </span>
              </Button>
            </Form.Item>
          </Form>

          <Divider 
            style={{
              margin: '2rem 0',
              borderColor: '#e5e7eb'
            }}
          >
            <span 
              style={{
                color: '#9ca3af',
                fontSize: '14px',
                fontWeight: '600',
                backgroundColor: 'white',
                padding: '0 16px'
              }}
            >
              OR
            </span>
          </Divider>

          {/* Google Login Button */}
          <div
            ref={googleButtonRef}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          />
          {googleLoading && (
            <div style={{ textAlign: 'center', marginTop: '8px', color: '#6b7280', fontSize: '14px' }}>
              Signing in with Google...
            </div>
          )}

          {/* Register Link */}
          <div 
            style={{
              textAlign: 'center',
              marginTop: '2rem'
            }}
          >
            <Text 
              style={{
                color: '#6b7280',
                fontSize: '15px'
              }}
            >
              Don't have an account?{' '}
              <Link 
                onClick={handleRegister}
                style={{
                  color: '#667eea',
                  fontWeight: '600',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#4f46e5';
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#667eea';
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                Sign up for free
              </Link>
            </Text>
          </div>
        </Card>

        {/* Footer Information */}
        <div 
          style={{
            textAlign: 'center',
            marginTop: '2rem'
          }}
        >
          <Text 
            style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '13px',
              lineHeight: '1.6'
            }}
          >
            By signing in, you agree to our{' '}
            <Link 
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link 
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Privacy Policy
            </Link>
          </Text>
        </div>
      </div>
    </div>
  );
};

export default Login; 